import React, { useEffect, useMemo, useRef, useState } from "react";
import { Receipt, Search, Download, FileText, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useOnFocus } from "../hooks/useOnFocus";

const TYPES = [
    { value: "all", label: "All Types" },
    { value: "subscription", label: "Subscription" },
    { value: "recharge", label: "Recharge" },
    { value: "usage", label: "Usage" },
    { value: "one_time", label: "One-time" },
];

const STATUSES = [
    { value: "all", label: "All Statuses" },
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
    { value: "failed", label: "Failed" },
    { value: "refunded", label: "Refunded" },
];

const statusStyle = (s) => {
    switch (s) {
        case "paid":
            return { bg: "rgba(34,197,94,0.12)", color: "#4ade80" };
        case "pending":
            return { bg: "rgba(245,158,11,0.12)", color: "#fbbf24" };
        case "failed":
            return { bg: "rgba(239,68,68,0.12)", color: "#fca5a5" };
        case "refunded":
            return { bg: "rgba(148,163,184,0.12)", color: "#cbd5e1" };
        default:
            return { bg: "rgba(148,163,184,0.12)", color: "#cbd5e1" };
    }
};

export default function Payments() {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState("all");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [downloading, setDownloading] = useState(null);
    const [freshIds, setFreshIds] = useState(new Set());
    const knownIdsRef = useRef(new Set());

    useEffect(() => {
        api.payments()
            .then((d) => {
                const list = d.payments || [];
                setPayments(list);
                knownIdsRef.current = new Set(list.map((p) => p.id));
            })
            .catch((err) => toast.error(err?.message || "Failed to load transactions"))
            .finally(() => setLoading(false));
    }, []);

    // Silent refresh on tab focus — surfaces new payments that landed elsewhere.
    useOnFocus(async () => {
        try {
            const d = await api.payments();
            const list = d.payments || [];
            const newOnes = list.filter((p) => !knownIdsRef.current.has(p.id));
            setPayments(list);
            if (newOnes.length > 0) {
                knownIdsRef.current = new Set(list.map((p) => p.id));
                setFreshIds(new Set(newOnes.map((p) => p.id)));
                toast.success(
                    newOnes.length === 1
                        ? `New payment: ${newOnes[0].description}`
                        : `${newOnes.length} new payments landed`,
                );
                // Clear the highlight after animation
                setTimeout(() => setFreshIds(new Set()), 3000);
            }
        } catch {
            /* silent */
        }
    });

    const filtered = useMemo(() => {
        return payments.filter((p) => {
            if (type !== "all" && p.type !== type) return false;
            if (status !== "all" && p.status !== status) return false;
            if (q && !`${p.description} ${p.reference}`.toLowerCase().includes(q.toLowerCase()))
                return false;
            return true;
        });
    }, [payments, type, status, q]);

    const total = filtered.reduce(
        (s, p) => s + (p.status === "paid" && p.direction !== "credit" ? p.amount : 0),
        0,
    );

    const downloadReceipt = async (id) => {
        setDownloading(id);
        try {
            await api.downloadReceipt(id);
            toast.success("Receipt downloaded");
        } catch (err) {
            toast.error(err?.message || "Failed to download receipt");
        } finally {
            setDownloading(null);
        }
    };

    const exportCsv = () => {
        const rows = [
            ["Date", "Description", "Type", "Method", "Reference", "Amount", "Status"],
            ...filtered.map((p) => [
                new Date(p.date).toISOString(),
                p.description,
                p.type,
                p.method,
                p.reference,
                p.amount,
                p.status,
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `payments-${Date.now()}.csv`;
        a.click();
    };

    return (
        <div className="flex flex-col gap-6 fade-in" data-testid="payments-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div
                        className="ap-label mb-1"
                        style={{ color: "var(--ap-blue-2)" }}
                    >
                        Auto Shop Owner / Manager
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                        Payment History
                    </h1>
                    <p className="text-sm mt-1" style={{ color: "var(--ap-text-dim)" }}>
                        Every transaction associated with your shop.
                    </p>
                </div>
                <button
                    data-testid="export-csv-btn"
                    onClick={exportCsv}
                    className="ap-btn-ghost text-sm flex items-center gap-2"
                >
                    <Download size={16} /> Export CSV
                </button>
            </div>

            {/* Filters */}
            <div className="ap-card p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2 relative">
                    <Search
                        size={16}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                        data-testid="payment-search"
                        placeholder="Search description or reference..."
                        className="ap-input pl-10"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
                <select
                    data-testid="filter-type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="ap-input"
                >
                    {TYPES.map((t) => (
                        <option key={t.value} value={t.value} className="bg-slate-900">
                            {t.label}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="filter-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="ap-input"
                >
                    {STATUSES.map((t) => (
                        <option key={t.value} value={t.value} className="bg-slate-900">
                            {t.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="ap-card-soft p-5">
                    <div className="ap-label mb-1">Total transactions</div>
                    <div className="text-3xl font-bold text-white">{filtered.length}</div>
                </div>
                <div className="ap-card-soft p-5">
                    <div className="ap-label mb-1">Paid total</div>
                    <div className="text-3xl font-bold" style={{ color: "var(--ap-green)" }}>
                        ${total.toFixed(2)}
                    </div>
                </div>
                <div className="ap-card-soft p-5 flex items-center gap-3">
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: "var(--ap-blue-soft)", color: "var(--ap-blue-2)" }}
                    >
                        <Receipt size={22} />
                    </div>
                    <div>
                        <div className="ap-label mb-1">Method</div>
                        <div className="text-base text-white font-semibold">PayPal</div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="ap-card p-2">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="payments-table">
                        <thead>
                            <tr
                                className="text-left text-xs uppercase tracking-wide"
                                style={{ color: "var(--ap-text-dim)" }}
                            >
                                <th className="px-4 py-3 font-semibold">Date</th>
                                <th className="px-4 py-3 font-semibold">Description</th>
                                <th className="px-4 py-3 font-semibold">Type</th>
                                <th className="px-4 py-3 font-semibold">Method</th>
                                <th className="px-4 py-3 font-semibold">Reference</th>
                                <th className="px-4 py-3 font-semibold text-right">Amount</th>
                                <th className="px-4 py-3 font-semibold text-right">Status</th>
                                <th className="px-4 py-3 font-semibold text-right">Receipt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={8} className="text-center py-10 text-slate-500">
                                        Loading...
                                    </td>
                                </tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-10 text-slate-500">
                                        No payments match your filters.
                                    </td>
                                </tr>
                            )}
                            {filtered.map((p) => {
                                const s = statusStyle(p.status);
                                return (
                                    <tr
                                        key={p.id}
                                        className={`border-t hover:bg-white/[0.02] transition-colors ${
                                            freshIds.has(p.id) ? "ap-row-fresh" : ""
                                        }`}
                                        style={{ borderColor: "var(--ap-line)" }}
                                        data-testid={`payment-row-${p.id}`}
                                    >
                                        <td className="px-4 py-4 text-slate-300 whitespace-nowrap">
                                            {new Date(p.date).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-4 text-white font-medium">
                                            <div className="flex items-center gap-2">
                                                {p.direction === "credit" ? (
                                                    <ArrowDownLeft size={14} className="text-green-400" />
                                                ) : (
                                                    <ArrowUpRight size={14} className="text-red-400" />
                                                )}
                                                {p.description}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 capitalize text-slate-400">
                                            {p.type.replace("_", " ")}
                                        </td>
                                        <td className="px-4 py-4 text-slate-300">{p.method}</td>
                                        <td className="px-4 py-4 font-mono text-xs text-slate-400">
                                            {p.reference}
                                        </td>
                                        <td
                                            className="px-4 py-4 text-right font-semibold whitespace-nowrap"
                                            style={{
                                                color:
                                                    p.direction === "credit"
                                                        ? "var(--ap-green)"
                                                        : "white",
                                            }}
                                        >
                                            {p.direction === "credit" ? "+" : "-"}$
                                            {p.amount.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-4 text-right">
                                            <span
                                                className="px-2.5 py-1 rounded-md text-xs font-semibold capitalize"
                                                style={{ background: s.bg, color: s.color }}
                                            >
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-right">
                                            <button
                                                data-testid={`download-receipt-${p.id}`}
                                                disabled={downloading === p.id || !p.has_receipt}
                                                onClick={() => downloadReceipt(p.id)}
                                                className="ap-btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3 disabled:opacity-50"
                                                title="Download PDF receipt"
                                            >
                                                {downloading === p.id ? (
                                                    <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-500 border-t-transparent animate-spin" />
                                                ) : (
                                                    <FileText size={12} />
                                                )}
                                                Receipt
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
